/** Asks for the signed-in user's name when the sign-in gave none (an External ID account without a display name):
 * the team list, comments, @mentions and invitation emails show it. Saved with PATCH /v1/me. */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { UserRound } from "lucide-react";
import { useState } from "react";
import { api, errorMessage, unwrap } from "../api/client";
import { useOrg } from "../api/org";
import { useT, useIntlShape } from "../i18n/I18nProvider";
import { Button, TextInput, useToast } from "./ui";

export function useSaveName() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => unwrap(api.PATCH("/v1/me", { body: { display_name: name.trim() } })),
    onSuccess: (me) => qc.setQueryData(["me"], me),
  });
}

/** A one-line card above the page while the user has no name. */
export function NamePrompt() {
  const t = useT();
  const intl = useIntlShape();
  const { me } = useOrg();
  const save = useSaveName();
  const toast = useToast();
  const [name, setName] = useState("");
  if (me.user.display_name) return null;
  return (
    <form
      className="card name-prompt"
      data-testid="name-prompt"
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim()) save.mutate(name, { onSuccess: () => toast(t("auth.name.saved")), onError: (err) => toast(errorMessage(err), true) });
      }}
    >
      <UserRound size={18} className="faint" />
      <span className="small">
        {intl.formatMessage({ id: "auth.name.prompt" }, { b: (c) => <b>{c}</b> })}
      </span>
      <TextInput value={name} onChange={(e) => setName(e.target.value)} maxLength={100} placeholder={t("auth.name.placeholder")} aria-label={t("auth.name.label")} autoComplete="name" />
      <Button type="submit" variant="primary" disabled={!name.trim()} loading={save.isPending}>
        {t("common.save")}
      </Button>
    </form>
  );
}
